import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  AppointmentResponse,
  BranchResponse,
  CreateBranchRequest,
  CreateHolidayRequest,
  CreateRoomRequest,
  HolidayResponse,
  RoomResponse,
  RoomStatus,
  RoomType,
  UpdateBranchRequest,
  UpdateRoomRequest,
  WorkingHoursDay,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { pointOf, samePoint, type GeoPoint } from '../components/location/coordinates';
import { LocationPicker } from '../components/location/LocationPicker';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';
import { useLocalisedName } from '../i18n/localisedName';
import { AffectedBookings } from './scheduling/AffectedBookings';

/**
 * Branches, and everything a branch is configured with.
 *
 * Rooms, opening hours and closures live on this screen rather than on three of their own because
 * they are one job: setting a branch up. A clinic administrator doing it for the first time should
 * not have to discover that the calendar is somewhere else.
 *
 * Activation is deliberately a separate, explicit action rather than a checkbox on the create form.
 * It is what Laseera bills for (RULE-016), so it should be something someone chose to do.
 */
export function BranchesPage() {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.has('BRANCH_MANAGE');

  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const branches = useApiQuery<BranchResponse[]>(
    queryKeys.branches(clinicId ?? ''),
    `/clinics/${clinicId}/branches`,
    { enabled: Boolean(clinicId) },
  );

  const createBranch = useApiMutation<BranchResponse, CreateBranchRequest>(
    (body) => ({ path: `/clinics/${clinicId}/branches`, options: { method: 'POST', body } }),
    { invalidates: [queryKeys.branches(clinicId ?? '')], onSuccess: () => setCreating(false) },
  );

  const setActivation = useApiMutation<BranchResponse, { readonly branchId: string; readonly active: boolean }>(
    ({ branchId, active }) => ({
      path: `/clinics/${clinicId}/branches/${branchId}/activation`,
      options: { method: active ? 'PUT' : 'DELETE' },
    }),
    { invalidates: [queryKeys.branches(clinicId ?? '')] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.branches')} description={t('home.noClinic')} />;
  }

  const selectedBranch = branches.data?.find((branch) => branch.id === selectedBranchId);

  return (
    <>
      <PageHeader
        title={t('nav.items.branches')}
        description={t('branches.description')}
        actions={
          canManage ? (
            <button type="button" className="button button--primary" onClick={() => setCreating((open) => !open)}>
              {creating ? t('common.cancel') : t('branches.add')}
            </button>
          ) : null
        }
      />

      {creating ? (
        <BranchForm
          onSubmit={(body) => createBranch.mutate(body)}
          pending={createBranch.isPending}
          error={createBranch.error}
        />
      ) : null}

      <AsyncSection
        query={branches}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('branches.emptyTitle')}
        emptyDescription={t('branches.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('branches.code')}</th>
                  <th scope="col">{t('branches.name')}</th>
                  <th scope="col">{t('branches.city')}</th>
                  <th scope="col">{t('branches.rooms')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((branch) => (
                  <tr key={branch.id}>
                    <th scope="row" className="ltr">
                      {branch.code}
                    </th>
                    <td>{localised.name(branch)}</td>
                    <td>{branch.city ?? '—'}</td>
                    <td className="numeric">{branch.roomCount}</td>
                    <td>
                      <span className={branch.status === 'ACTIVE' ? 'badge' : 'badge badge--muted'}>
                        {t(`branches.status.${branch.status ?? 'INACTIVE'}`)}
                      </span>
                    </td>
                    <td>
                      <div className="table__actions">
                        <button
                          type="button"
                          className="button button--ghost"
                          onClick={() =>
                            setSelectedBranchId((current) => (current === branch.id ? null : (branch.id ?? null)))
                          }
                        >
                          {selectedBranchId === branch.id
                            ? t('branches.hideSetup')
                            : canManage
                              ? t('branches.edit')
                              : t('branches.view')}
                        </button>
                        {canManage ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            disabled={setActivation.isPending}
                            onClick={() =>
                              setActivation.mutate({
                                branchId: branch.id ?? '',
                                active: branch.status !== 'ACTIVE',
                              })
                            }
                          >
                            {branch.status === 'ACTIVE' ? t('branches.deactivate') : t('branches.activate')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {setActivation.error ? <ErrorState error={setActivation.error} /> : null}

      {selectedBranch ? (
        <BranchSetup clinicId={clinicId} branch={selectedBranch} canManage={canManage} />
      ) : null}
    </>
  );
}

// ── Create a branch ─────────────────────────────────────────────────────────

function BranchForm({
  onSubmit,
  pending,
  error,
}: {
  readonly onSubmit: (body: CreateBranchRequest) => void;
  readonly pending: boolean;
  readonly error: unknown;
}) {
  const { t } = useTranslation();
  // Required here although the API takes a branch without one: this form is where a clinic says
  // where its branch is, and a branch nobody can find on a map is what the pin exists to prevent.
  const [location, setLocation] = useState<GeoPoint | null>(null);
  const [locationMissing, setLocationMissing] = useState(false);

  return (
    <form
      className="panel"
      style={{ marginBlockEnd: 'var(--space-5)' }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!location) {
          setLocationMissing(true);
          return;
        }
        const form = new FormData(event.currentTarget);
        // No code: the server gives the next one, e.g. CL01BR03 (CR-24).
        onSubmit({
          nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
          nameEn: String(form.get('nameEn') ?? '').trim() || undefined,
          city: String(form.get('city') ?? '').trim() || undefined,
          addressLine: String(form.get('addressLine') ?? '').trim() || undefined,
          phone: String(form.get('phone') ?? '').trim() || undefined,
          latitude: location.latitude,
          longitude: location.longitude,
        });
      }}
    >
      <div className="form-grid">
        <Field name="nameAr" label={t('common.nameAr')} />
        <Field name="nameEn" label={t('common.nameEn')} dir="ltr" />
        <Field name="city" label={t('branches.city')} />
        <Field name="addressLine" label={t('branches.address')} />
        <Field name="phone" label={t('branches.phone')} dir="ltr" />
      </div>

      <div className="field" role="group" aria-labelledby="new-branch-location">
        <span className="field__label" id="new-branch-location">
          {t('branches.location')}
        </span>
        <LocationPicker
          value={location}
          onChange={(point) => {
            setLocation(point);
            if (point) {
              setLocationMissing(false);
            }
          }}
          error={locationMissing ? t('branches.locationRequired') : null}
        />
      </div>

      {error ? <ErrorState error={error} /> : null}

      <div style={{ marginBlockStart: 'var(--space-4)' }}>
        <button type="submit" className="button button--primary" disabled={pending}>
          {pending ? t('branches.saving') : t('branches.create')}
        </button>
        <p className="field__hint">{t('branches.createHint')}</p>
      </div>
    </form>
  );
}

// ── Rooms, hours and closures for one branch ────────────────────────────────

function BranchSetup({
  clinicId,
  branch,
  canManage,
}: {
  readonly clinicId: string;
  readonly branch: BranchResponse;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  const branchId = branch.id ?? '';

  return (
    <>
      <DetailsSection key={branchId} clinicId={clinicId} branch={branch} canManage={canManage} />
      <LocationSection clinicId={clinicId} branch={branch} canManage={canManage} />
      <RoomsSection clinicId={clinicId} branchId={branchId} canManage={canManage} />
      <WorkingHoursSection clinicId={clinicId} branchId={branchId} canManage={canManage} />
      <HolidaysSection clinicId={clinicId} branchId={branchId} canManage={canManage} />
      <p className="field__hint" style={{ marginBlockStart: 'var(--space-4)' }}>
        {t('branches.setupHint')}
      </p>
    </>
  );
}

/**
 * The names, city, address and phone the branch was created with, editable afterwards (CR-13).
 *
 * The code is shown and not editable: it is printed on receipts and keys imports, and the API has
 * no way to change it. Like the pin, these go through a PATCH that replaces the details as a whole,
 * so the pin is sent back unchanged alongside them.
 */
function DetailsSection({
  clinicId,
  branch,
  canManage,
}: {
  readonly clinicId: string;
  readonly branch: BranchResponse;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  // Bumped by Cancel, so the uncontrolled fields go back to what is saved.
  const [generation, setGeneration] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = useApiMutation<BranchResponse, UpdateBranchRequest>(
    (body) => ({ path: `/clinics/${clinicId}/branches/${branch.id}`, options: { method: 'PATCH', body } }),
    {
      invalidates: [queryKeys.branches(clinicId)],
      onSuccess: () => {
        setDirty(false);
        setSaved(true);
      },
    },
  );

  return (
    <section className="section" aria-labelledby="details-heading">
      <h2 className="section__title" id="details-heading">
        {t('branches.detailsTitle')}
      </h2>

      <form
        key={generation}
        onChange={() => {
          setDirty(true);
          setSaved(false);
        }}
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const text = (name: string) => String(form.get(name) ?? '').trim() || undefined;
          save.mutate({
            nameAr: text('nameAr'),
            nameEn: text('nameEn'),
            city: text('city'),
            addressLine: text('addressLine'),
            phone: text('phone'),
            latitude: branch.latitude,
            longitude: branch.longitude,
          });
        }}
      >
        <div className="form-grid">
          <div className="field">
            <span className="field__label">{t('branches.code')}</span>
            <span className="ltr">{branch.code}</span>
            <span className="field__hint">{t('branches.codeFixed')}</span>
          </div>
          <Field name="nameAr" label={t('common.nameAr')} defaultValue={branch.nameAr} readOnly={!canManage} />
          <Field
            name="nameEn"
            label={t('common.nameEn')}
            defaultValue={branch.nameEn}
            readOnly={!canManage}
            dir="ltr"
          />
          <Field name="city" label={t('branches.city')} defaultValue={branch.city} readOnly={!canManage} />
          <Field
            name="addressLine"
            label={t('branches.address')}
            defaultValue={branch.addressLine}
            readOnly={!canManage}
          />
          <Field
            name="phone"
            label={t('branches.phone')}
            defaultValue={branch.phone}
            readOnly={!canManage}
            dir="ltr"
          />
        </div>

        {save.error ? <ErrorState error={save.error} /> : null}

        {canManage ? (
          <div className="toolbar" style={{ marginBlockStart: 'var(--space-2)' }}>
            <button type="submit" className="button button--primary" disabled={!dirty || save.isPending}>
              {save.isPending ? t('branches.saving') : t('branches.saveDetails')}
            </button>
            <button
              type="button"
              className="button button--ghost"
              disabled={!dirty}
              onClick={() => {
                setGeneration((value) => value + 1);
                setDirty(false);
              }}
            >
              {t('common.cancel')}
            </button>
            {saved ? (
              <span className="field__hint" role="status">
                {t('branches.detailsSaved')}
              </span>
            ) : null}
          </div>
        ) : null}
      </form>
    </section>
  );
}

/**
 * The branch's pin, for placing a branch created before pins existed and for moving one that was
 * dropped in the wrong street.
 *
 * The API's PATCH replaces a branch's details as a whole, so saving the pin sends the rest of them
 * back unchanged alongside it.
 */
function LocationSection({
  clinicId,
  branch,
  canManage,
}: {
  readonly clinicId: string;
  readonly branch: BranchResponse;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  const saved = pointOf(branch);
  // undefined: nothing changed yet. null: the pin was taken away and nothing put in its place.
  const [draft, setDraft] = useState<GeoPoint | null | undefined>(undefined);
  // Bumped by Cancel, so the picker drops whatever was half-typed in its coordinates field.
  const [generation, setGeneration] = useState(0);

  const save = useApiMutation<BranchResponse, UpdateBranchRequest>(
    (body) => ({ path: `/clinics/${clinicId}/branches/${branch.id}`, options: { method: 'PATCH', body } }),
    { invalidates: [queryKeys.branches(clinicId)], onSuccess: () => setDraft(undefined) },
  );

  const shown = draft === undefined ? saved : draft;
  const changed = draft !== undefined && !samePoint(draft, saved);

  return (
    <section className="section" aria-labelledby="location-heading">
      <h2 className="section__title" id="location-heading">
        {t('branches.location')}
      </h2>

      <LocationPicker
        key={`${branch.id}:${generation}`}
        value={shown}
        onChange={setDraft}
        readOnly={!canManage}
      />

      {save.error ? <ErrorState error={save.error} /> : null}

      {canManage ? (
        <div className="toolbar" style={{ marginBlockStart: 'var(--space-4)' }}>
          <button
            type="button"
            className="button button--primary"
            disabled={!changed || !draft || save.isPending}
            onClick={() => {
              if (!draft) {
                return;
              }
              save.mutate({
                nameEn: branch.nameEn,
                nameAr: branch.nameAr,
                city: branch.city,
                addressLine: branch.addressLine,
                phone: branch.phone,
                latitude: draft.latitude,
                longitude: draft.longitude,
              });
            }}
          >
            {save.isPending ? t('branches.saving') : t('branches.saveLocation')}
          </button>
          <button
            type="button"
            className="button button--ghost"
            disabled={draft === undefined}
            onClick={() => {
              setDraft(undefined);
              setGeneration((value) => value + 1);
            }}
          >
            {t('common.cancel')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function RoomsSection({
  clinicId,
  branchId,
  canManage,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly canManage: boolean;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const typeList = useRoomTypeList();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTypes, setNewTypes] = useState<RoomType[]>(['LASER']);

  const rooms = useApiQuery<RoomResponse[]>(
    queryKeys.rooms(clinicId, branchId),
    `/clinics/${clinicId}/branches/${branchId}/rooms`,
  );
  const createRoom = useApiMutation<RoomResponse, CreateRoomRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/branches/${branchId}/rooms`,
      options: { method: 'POST', body },
    }),
    {
      invalidates: [queryKeys.rooms(clinicId, branchId), queryKeys.branches(clinicId)],
      onSuccess: () => setNewTypes(['LASER']),
    },
  );

  return (
    <section className="section" aria-labelledby="rooms-heading">
      <h2 className="section__title" id="rooms-heading">
        {t('branches.roomsTitle')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('branches.roomTypeHint')}
      </p>

      <AsyncSection
        query={rooms}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('branches.noRooms')}
        emptyDescription={t('branches.noRoomsDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('branches.code')}</th>
                  <th scope="col">{t('branches.name')}</th>
                  <th scope="col">{t('branches.roomType')}</th>
                  <th scope="col">{t('common.status')}</th>
                  {canManage ? <th scope="col">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((room) =>
                  editingId === room.id ? (
                    <RoomEditRow
                      key={room.id}
                      clinicId={clinicId}
                      branchId={branchId}
                      room={room}
                      onDone={() => setEditingId(null)}
                    />
                  ) : (
                    <tr key={room.id}>
                      <th scope="row" className="ltr">
                        {room.code}
                      </th>
                      <td>{localised.name(room)}</td>
                      <td>{typeList(typesOfRoom(room))}</td>
                      <td>
                        <span className={room.status === 'ACTIVE' ? 'badge' : 'badge badge--muted'}>
                          {t(`branches.status.${room.status ?? 'INACTIVE'}`)}
                        </span>
                      </td>
                      {canManage ? (
                        <td>
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => setEditingId(room.id ?? null)}
                          >
                            {t('common.edit')}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {canManage ? (
        <>
          <form
            className="toolbar"
            style={{ marginBlockStart: 'var(--space-4)' }}
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              // No code: the server gives the branch's next one, e.g. CL01BR01RM02 (CR-24).
              createRoom.mutate({
                nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
                roomTypes: newTypes,
              });
              event.currentTarget.reset();
            }}
          >
            <Field name="nameAr" label={t('branches.name')} />
            <RoomTypePicker
              label={t('branches.roomType')}
              options={ROOM_TYPES}
              value={newTypes}
              onChange={setNewTypes}
            />
            <button
              type="submit"
              className="button button--ghost"
              disabled={createRoom.isPending || newTypes.length === 0}
            >
              {t('branches.addRoom')}
            </button>
          </form>
          <p className="field__hint">{t('branches.roomCodeHint')}</p>
        </>
      ) : null}

      {createRoom.error ? <ErrorState error={createRoom.error} /> : null}
    </section>
  );
}

/**
 * What a room can be (CR-21): the types follow the service categories. "TREATMENT", the old shared
 * type, is not offered for a new room; a room that still has it can keep it until it is re-typed.
 */
const ROOM_TYPES: RoomType[] = ['LASER', 'DERMATOLOGY', 'FACIAL', 'BODY', 'CONSULTATION'];
const ROOM_STATUSES: RoomStatus[] = ['ACTIVE', 'INACTIVE'];

/** A room's types (CR-23), or its single type from a server that predates them. */
function typesOfRoom(room: RoomResponse): RoomType[] {
  const several = (room.roomTypes ?? []).filter((type): type is RoomType => Boolean(type));
  if (several.length > 0) {
    return several;
  }
  return room.roomType ? [room.roomType] : [];
}

/** "Laser and Dermatology", «ليزر وجلدية»: listed the reader's own way. */
function useRoomTypeList() {
  const { t, i18n } = useTranslation();
  return (types: readonly RoomType[]) =>
    new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(
      types.map((type) => t(`branches.roomTypes.${type}`)),
    );
}

/**
 * Ticks for every kind of treatment a room serves (CR-23). At least one: a room for nothing should
 * be marked out of use instead. What is sent follows the order of the options, not of the clicks.
 */
function RoomTypePicker({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly RoomType[];
  readonly value: readonly RoomType[];
  readonly onChange: (next: RoomType[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="field__label">{label}</legend>
      <div className="toolbar">
        {options.map((type) => (
          <label key={type} className="field__label">
            <input
              type="checkbox"
              checked={value.includes(type)}
              onChange={(event) =>
                onChange(
                  options.filter((option) => (option === type ? event.target.checked : value.includes(option))),
                )
              }
            />{' '}
            {t(`branches.roomTypes.${type}`)}
          </label>
        ))}
      </div>
      {value.length === 0 ? <span className="field__hint">{t('branches.needsRoomType')}</span> : null}
    </fieldset>
  );
}

/**
 * One room, being edited in place (CR-21, CR-23): its name, what it is used for and whether it is in
 * use. Until CR-21 a room could only be added, so a room entered with the wrong type stayed that way.
 * The API replaces the details as a whole, so the English name, which this screen does not edit, is
 * sent back as it was.
 */
function RoomEditRow({
  clinicId,
  branchId,
  room,
  onDone,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly room: RoomResponse;
  readonly onDone: () => void;
}) {
  const { t } = useTranslation();
  const [nameAr, setNameAr] = useState(room.nameAr ?? '');
  const [roomTypes, setRoomTypes] = useState<RoomType[]>(typesOfRoom(room));
  const [status, setStatus] = useState<RoomStatus>(room.status ?? 'ACTIVE');

  const save = useApiMutation<RoomResponse, UpdateRoomRequest>(
    (body) => ({ path: `/clinics/${clinicId}/rooms/${room.id}`, options: { method: 'PATCH', body } }),
    { invalidates: [queryKeys.rooms(clinicId, branchId), queryKeys.branches(clinicId)], onSuccess: onDone },
  );

  // The old shared type stays tickable on a room that still has it, and nowhere else.
  const typeOptions: RoomType[] = typesOfRoom(room).includes('TREATMENT') ? [...ROOM_TYPES, 'TREATMENT'] : ROOM_TYPES;

  return (
    <tr>
      <th scope="row" className="ltr">
        {room.code}
      </th>
      <td>
        <input
          className="field__input"
          aria-label={`${t('branches.name')} ${room.code ?? ''}`}
          value={nameAr}
          onChange={(event) => setNameAr(event.target.value)}
        />
      </td>
      <td>
        <RoomTypePicker
          label={`${t('branches.roomType')} ${room.code ?? ''}`}
          options={typeOptions}
          value={roomTypes}
          onChange={setRoomTypes}
        />
        {roomTypes.includes('TREATMENT') ? <span className="field__hint">{t('branches.roomTypeOld')}</span> : null}
      </td>
      <td>
        <select
          className="field__input"
          aria-label={`${t('common.status')} ${room.code ?? ''}`}
          value={status}
          onChange={(event) => setStatus(event.target.value as RoomStatus)}
        >
          {ROOM_STATUSES.map((option) => (
            <option key={option} value={option}>
              {t(`branches.status.${option}`)}
            </option>
          ))}
        </select>
        {save.error ? <ErrorState error={save.error} /> : null}
      </td>
      <td>
        <div className="toolbar">
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending || roomTypes.length === 0}
            onClick={() =>
              save.mutate({ nameEn: room.nameEn, nameAr: nameAr.trim() || undefined, roomTypes, status })
            }
          >
            {t('branches.saveRoom')}
          </button>
          <button type="button" className="button button--ghost" onClick={onDone}>
            {t('common.cancel')}
          </button>
        </div>
      </td>
    </tr>
  );
}

function WorkingHoursSection({
  clinicId,
  branchId,
  canManage,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<WorkingHoursDay[] | null>(null);
  // The week waiting on the administrator's answer, and the bookings it would cancel.
  const [confirming, setConfirming] = useState<{
    readonly week: WorkingHoursDay[];
    readonly affected: AppointmentResponse[];
  } | null>(null);
  const [cancelled, setCancelled] = useState(0);

  const hours = useApiQuery<WorkingHoursDay[]>(
    queryKeys.workingHours(clinicId, branchId),
    `/clinics/${clinicId}/branches/${branchId}/working-hours`,
  );

  const check = useApiMutation<AppointmentResponse[], WeekRequest>((body) => ({
    path: `/clinics/${clinicId}/branches/${branchId}/working-hours/impact`,
    options: { method: 'POST', body },
  }));

  const save = useApiMutation<WorkingHoursDay[], WeekRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/branches/${branchId}/working-hours`,
      options: { method: 'PUT', body },
    }),
    {
      invalidates: [queryKeys.workingHours(clinicId, branchId), ...calendarDependents(clinicId)],
      onSuccess: () => {
        setDraft(null);
        setConfirming(null);
      },
    },
  );

  // Asks first. A week that cancels nobody saves straight away; one that would cancel somebody
  // waits for the administrator to see who.
  const start = async (week: WorkingHoursDay[]) => {
    setCancelled(0);
    const request = { days: week, cancelAffectedAppointments: false };
    try {
      const affected = await check.mutateAsync(request);
      if (affected.length === 0) {
        save.mutate(request);
      } else {
        setConfirming({ week, affected });
      }
    } catch {
      // Shown below from check.error.
    }
  };

  return (
    <section className="section" aria-labelledby="hours-heading">
      <h2 className="section__title" id="hours-heading">
        {t('branches.hoursTitle')}
      </h2>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('branches.hoursHint')}
      </p>

      <AsyncSection query={hours}>
        {(days) => {
          const week = draft ?? days;
          const update = (day: WorkingHoursDay['day'], change: Partial<WorkingHoursDay>) =>
            setDraft(week.map((entry) => (entry.day === day ? { ...entry, ...change } : entry)));

          return (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('branches.day')}</th>
                      <th scope="col">{t('branches.open')}</th>
                      <th scope="col">{t('branches.opensAt')}</th>
                      <th scope="col">{t('branches.closesAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week.map((entry) => (
                      <tr key={entry.day}>
                        <th scope="row">{t(`branches.days.${entry.day ?? 'SATURDAY'}`)}</th>
                        <td>
                          <input
                            type="checkbox"
                            checked={!entry.closed}
                            disabled={!canManage}
                            aria-label={t('branches.open')}
                            onChange={(event) =>
                              update(entry.day, {
                                closed: !event.target.checked,
                                opensAt: event.target.checked ? (entry.opensAt ?? '10:00:00') : undefined,
                                closesAt: event.target.checked ? (entry.closesAt ?? '22:00:00') : undefined,
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            className="field__input numeric"
                            value={toTimeInput(entry.opensAt)}
                            disabled={!canManage || Boolean(entry.closed)}
                            aria-label={t('branches.opensAt')}
                            onChange={(event) => update(entry.day, { opensAt: `${event.target.value}:00` })}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            className="field__input numeric"
                            value={toTimeInput(entry.closesAt)}
                            disabled={!canManage || Boolean(entry.closed)}
                            aria-label={t('branches.closesAt')}
                            onChange={(event) => update(entry.day, { closesAt: `${event.target.value}:00` })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {check.error ? <ErrorState error={check.error} /> : null}

              {confirming ? (
                <AffectedBookings
                  affected={confirming.affected}
                  explanation={t('branches.affected.explanation')}
                  title={t('branches.affected.hoursTitle', { count: confirming.affected.length })}
                  confirmLabel={t('branches.affected.confirmHours', { count: confirming.affected.length })}
                  pending={save.isPending}
                  error={save.error}
                  onConfirm={() => {
                    const count = confirming.affected.length;
                    save.mutate(
                      { days: confirming.week, cancelAffectedAppointments: true },
                      { onSuccess: () => setCancelled(count) },
                    );
                  }}
                  onBack={() => setConfirming(null)}
                />
              ) : (
                <>
                  {save.error ? <ErrorState error={save.error} /> : null}
                  {canManage ? (
                    <div className="toolbar" style={{ marginBlockStart: 'var(--space-4)' }}>
                      <button
                        type="button"
                        className="button button--primary"
                        disabled={draft === null || check.isPending || save.isPending}
                        onClick={() => void start(week)}
                      >
                        {check.isPending || save.isPending ? t('branches.saving') : t('branches.saveWeek')}
                      </button>
                      <button
                        type="button"
                        className="button button--ghost"
                        disabled={draft === null}
                        onClick={() => setDraft(null)}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  ) : null}
                </>
              )}

              {cancelled > 0 ? (
                <p className="field__hint" role="status">
                  {t('branches.affected.done', { count: cancelled })}
                </p>
              ) : null}
            </>
          );
        }}
      </AsyncSection>
    </section>
  );
}

function HolidaysSection({
  clinicId,
  branchId,
  canManage,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly canManage: boolean;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  // The closure waiting on the administrator's answer, and the bookings it would cancel.
  const [confirming, setConfirming] = useState<{
    readonly request: CreateHolidayRequest;
    readonly affected: AppointmentResponse[];
  } | null>(null);
  const [cancelled, setCancelled] = useState(0);

  const holidays = useApiQuery<HolidayResponse[]>(
    queryKeys.holidays(clinicId, branchId),
    `/clinics/${clinicId}/branches/${branchId}/holidays`,
  );

  const check = useApiMutation<AppointmentResponse[], string>((on) => ({
    path: `/clinics/${clinicId}/branches/${branchId}/holidays/impact?on=${encodeURIComponent(on)}`,
    options: { method: 'GET' },
  }));

  const addHoliday = useApiMutation<HolidayResponse, CreateHolidayRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/branches/${branchId}/holidays`,
      options: { method: 'POST', body },
    }),
    {
      invalidates: [queryKeys.holidays(clinicId, branchId), ...calendarDependents(clinicId)],
      onSuccess: () => {
        formRef.current?.reset();
        setConfirming(null);
      },
    },
  );

  // Asks first, as the week does: an empty day closes straight away, a booked one waits.
  const start = async (request: CreateHolidayRequest) => {
    setCancelled(0);
    try {
      const affected = await check.mutateAsync(request.holidayOn);
      if (affected.length === 0) {
        addHoliday.mutate(request);
      } else {
        setConfirming({ request, affected });
      }
    } catch {
      // Shown below from check.error.
    }
  };

  const removeHoliday = useApiMutation<void, string>(
    (holidayId) => ({ path: `/clinics/${clinicId}/holidays/${holidayId}`, options: { method: 'DELETE' } }),
    { invalidates: [queryKeys.holidays(clinicId, branchId)] },
  );

  return (
    <section className="section" aria-labelledby="holidays-heading">
      <h2 className="section__title" id="holidays-heading">
        {t('branches.holidaysTitle')}
      </h2>

      <AsyncSection
        query={holidays}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('branches.noHolidays')}
        emptyDescription={t('branches.noHolidaysDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('branches.date')}</th>
                  <th scope="col">{t('branches.occasion')}</th>
                  {canManage ? <th scope="col">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((holiday) => (
                  <tr key={holiday.id}>
                    <th scope="row" className="numeric">
                      {holiday.holidayOn}
                    </th>
                    <td>{localised.name(holiday)}</td>
                    {canManage ? (
                      <td>
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={removeHoliday.isPending}
                          onClick={() => removeHoliday.mutate(holiday.id ?? '')}
                        >
                          {t('branches.reopen')}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {canManage ? (
        <form
          ref={formRef}
          className="toolbar"
          style={{ marginBlockStart: 'var(--space-4)' }}
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            void start({
              holidayOn: String(form.get('holidayOn') ?? ''),
              nameAr: String(form.get('nameAr') ?? '').trim() || undefined,
              cancelAffectedAppointments: false,
            });
          }}
        >
          <label className="field">
            <span className="field__label">{t('branches.date')}</span>
            <input type="date" name="holidayOn" className="field__input numeric" required />
          </label>
          <Field name="nameAr" label={t('branches.occasion')} />
          <button
            type="submit"
            className="button button--ghost"
            disabled={check.isPending || addHoliday.isPending || confirming !== null}
          >
            {t('branches.addHoliday')}
          </button>
        </form>
      ) : null}

      {check.error ? <ErrorState error={check.error} /> : null}

      {confirming ? (
        <AffectedBookings
          affected={confirming.affected}
          explanation={t('branches.affected.explanation')}
          title={t('branches.affected.closureTitle', { count: confirming.affected.length })}
          confirmLabel={t('branches.affected.confirmClosure', { count: confirming.affected.length })}
          pending={addHoliday.isPending}
          error={addHoliday.error}
          onConfirm={() => {
            const count = confirming.affected.length;
            addHoliday.mutate(
              { ...confirming.request, cancelAffectedAppointments: true },
              { onSuccess: () => setCancelled(count) },
            );
          }}
          onBack={() => setConfirming(null)}
        />
      ) : addHoliday.error ? (
        <ErrorState error={addHoliday.error} />
      ) : null}

      {cancelled > 0 ? (
        <p className="field__hint" role="status">
          {t('branches.affected.done', { count: cancelled })}
        </p>
      ) : null}
    </section>
  );
}

// ── Small helpers ───────────────────────────────────────────────────────────

/** A week as the API takes it, both to ask what it would cancel and to save it. */
interface WeekRequest {
  readonly days: WorkingHoursDay[];
  readonly cancelAffectedAppointments: boolean;
}

/**
 * What a closure or an hours change can touch besides the calendar: the bookings it cancelled, on
 * the day board, and the worklist opened to rebook them.
 */
function calendarDependents(clinicId: string): readonly (readonly unknown[])[] {
  return [
    ['clinics', clinicId, 'appointments'],
    ['clinics', clinicId, 'worklists'],
  ];
}

function Field({
  name,
  label,
  required,
  hint,
  dir,
  defaultValue,
  readOnly,
}: {
  readonly name: string;
  readonly label: string;
  readonly required?: boolean;
  readonly hint?: string;
  readonly dir?: 'ltr';
  readonly defaultValue?: string;
  readonly readOnly?: boolean;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        name={name}
        className="field__input"
        required={required}
        dir={dir}
        defaultValue={defaultValue}
        readOnly={readOnly}
      />
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

/** The API sends `HH:mm:ss`; a time input wants `HH:mm`. */
function toTimeInput(time: string | undefined): string {
  if (!time) {
    return '';
  }
  return time.slice(0, 5);
}
